import React, {
  ChangeEvent,
  useCallback,
  useMemo,
  useState,
  useEffect
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import dynamic from 'next/dynamic';
import { setError } from '../../actions';
import {
  fallbackStyles,
  ResidenceControlsWrapper,
  SearchPlaceholder,
  Spinner,
  SpinnerWrapper
} from './styled';
import { BusinessItemsListPageProps, BusinessItemsType } from './types';
import { getFilteredValue } from '../../utils/helpers/getFilteredValue';
import { PageWrapper } from '../../styled/pageLayout.styles';
import { AdaptivePageHeader } from '../ui/PageHeader/AdaptivePageHeader';

const NewInput = dynamic(
  () => import('../ui/Input/NewInput').then(mod => mod.NewInput),
  {
    ssr: false,
    loading: SearchPlaceholder
  }
);

const FallbackView = dynamic(
  () => import('../FallbackView').then(mod => mod.FallbackView),
  { ssr: false }
);

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
});

const BusinessItemsListPageWrapperComponent: React.FC<
  BusinessItemsListPageProps
> = ({
  setErrorUI,
  title,
  hasError,
  items,
  CardComponent,
  ListContainer,
  type = BusinessItemsType.agencies,
  children,
  loading = false,
  isMapView = false,
  mapView
}) => {
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState('');

  const filteredItems = useMemo(
    () => getFilteredValue(items, searchValue),
    [items, searchValue]
  );

  const itemList = useMemo(
    () =>
      filteredItems?.length ? (
        filteredItems?.map(item => <CardComponent key={item.id} {...item} />)
      ) : (
        <FallbackView
          width={213}
          height={155}
          text={t('pages.business_pages.no_results')}
          icon="/static/images/new_ad/icons/icon-not-found.png"
          styles={fallbackStyles}
        />
      ),
    [filteredItems, CardComponent, t]
  );

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) =>
    setSearchValue(e.target.value);

  const onResetHandler = useCallback(() => setSearchValue(''), []);

  useEffect(() => {
    if (hasError) setErrorUI(true);
  }, [hasError, setErrorUI]);

  const isEmptyView = !filteredItems?.length && !loading;
  const isResidencePage = type === BusinessItemsType.residences;
  const inputPlaceholder = t(
    isResidencePage ? 'residences.search' : 'pages.agencies.search'
  );

  const headerContent = (
    <>
      {!isMapView && (
        <NewInput
          stat={`search-${isResidencePage ? 'complex' : 'agency'}`}
          name={`${type}-page-search`}
          placeholder={inputPlaceholder}
          autoComplete="on"
          onChangeHandler={onChangeHandler}
          value={searchValue}
          isShowSearch
          isShowReset={!!searchValue}
          onResetHandler={onResetHandler}
        />
      )}
      {children}
    </>
  );

  return (
    <PageWrapper>
      <AdaptivePageHeader
        title={title}
        type={type}
        isResidencePage={isResidencePage}
      >
        {isResidencePage ? (
          <ResidenceControlsWrapper>{headerContent}</ResidenceControlsWrapper>
        ) : (
          headerContent
        )}
      </AdaptivePageHeader>
      {loading && !isMapView && (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      )}
      {!loading && !isMapView && (
        <ListContainer $isEmptyView={isEmptyView} data-cy={`${type}-page-list`}>
          {itemList}
        </ListContainer>
      )}
      {isMapView && mapView}
    </PageWrapper>
  );
};

export const BusinessItemsListPageWrapper = connect(
  null,
  mapDispatchToProps
)(BusinessItemsListPageWrapperComponent);
